import { Component, Inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { CommonModule } from "@angular/common";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { GlossScrollDirective } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.directive";

export interface CleanupGroupResult {
  kept: StoreData;
  deleted: StoreData[];
  /** When true, all copies including `kept` were deleted (none had a valid link). */
  allDeleted?: boolean;
}

export interface KeptSourceEntry {
  source: StoreData;
  linkedTransactions: StoreData[];
}

/** Account cleanup: removed transaction + a deleted account row it belonged to (acId). */
export interface AccountCascadeDeletedTx {
  transaction: StoreData;
  formerAccount: StoreData;
}

/** Account cleanup: removed source + a deleted transaction that referenced it (srId). */
export interface AccountCascadeDeletedSource {
  source: StoreData;
  formerTransaction: StoreData;
}

export interface VaultCleanupResultDialogData {
  groups: CleanupGroupResult[];
  deletedSourceTransactionCount?: number;
  deletedSourceTransactions?: StoreData[];
  keptSourceTransactions?: KeptSourceEntry[];
  /** Custom title for the source section. Defaults to "Source Transactions". */
  sourceSectionTitle?: string;
  /** When set, shows extra sections for cascaded tx / source deletes (vault-type-account). */
  accountCleanup?: {
    deletedTransactions: AccountCascadeDeletedTx[];
    deletedSources: AccountCascadeDeletedSource[];
  };
}

@Component({
  selector: "app-vault-cleanup-result-dialog",
  standalone: true,
  imports: [GlossScrollDirective, MatDialogModule, MatButtonModule, CommonModule],
  templateUrl: "./vault-cleanup-result-dialog.component.html",
})
export class VaultCleanupResultDialogComponent {
  expandedBeforeIndices = signal<Set<number>>(new Set());
  expandedSourceIndices = signal<Set<number>>(new Set());
  expandedAcctTxIndices = signal<Set<number>>(new Set());
  expandedAcctSrcIndices = signal<Set<number>>(new Set());

  constructor(@Inject(MAT_DIALOG_DATA) public data: VaultCleanupResultDialogData) {}

  get hasSourceSection(): boolean {
    return (
      (this.data.deletedSourceTransactions?.length ?? 0) > 0 ||
      (this.data.keptSourceTransactions?.length ?? 0) > 0
    );
  }

  get sourceSectionTitle(): string {
    return this.data.sourceSectionTitle ?? "Source Transactions";
  }

  /** True when only keptSourceTransactions is provided (no deleted side). */
  get isKeptOnlyLayout(): boolean {
    return (
      (this.data.keptSourceTransactions?.length ?? 0) > 0 &&
      (this.data.deletedSourceTransactions?.length ?? 0) === 0 &&
      !this.data.deletedSourceTransactionCount
    );
  }

  toggleBeforeExpand(index: number): void {
    const current = new Set(this.expandedBeforeIndices());
    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
    }
    this.expandedBeforeIndices.set(current);
  }

  isBeforeExpanded(index: number): boolean {
    return this.expandedBeforeIndices().has(index);
  }

  toggleSourceExpand(index: number): void {
    const current = new Set(this.expandedSourceIndices());
    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
    }
    this.expandedSourceIndices.set(current);
  }

  isSourceExpanded(index: number): boolean {
    return this.expandedSourceIndices().has(index);
  }

  itemJson(item: StoreData): string {
    try {
      return JSON.stringify(item, null, 2);
    } catch {
      return String(item.id);
    }
  }

  formatDc(item: StoreData): string {
    const dc = item.dc;
    if (!dc) {
      return "—";
    }
    try {
      return new Date(dc).toLocaleString();
    } catch {
      return String(dc);
    }
  }

  get hasAccountCascade(): boolean {
    return this.data.accountCleanup != null;
  }

  toggleAcctTxExpand(index: number): void {
    const current = new Set(this.expandedAcctTxIndices());
    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
    }
    this.expandedAcctTxIndices.set(current);
  }

  isAcctTxExpanded(index: number): boolean {
    return this.expandedAcctTxIndices().has(index);
  }

  toggleAcctSrcExpand(index: number): void {
    const current = new Set(this.expandedAcctSrcIndices());
    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
    }
    this.expandedAcctSrcIndices.set(current);
  }

  isAcctSrcExpanded(index: number): boolean {
    return this.expandedAcctSrcIndices().has(index);
  }
}
